<script setup lang="ts">
import type { Recommendations } from '@functions/types/recommendations';
import { captureException } from '@sentry/vue';
import { httpsCallable } from 'firebase/functions';
import { onMounted, ref } from 'vue';
import { useDebugUser } from '@/composables/useDebugUser';
import { functions } from '@/firebase';
import Container from '@/layouts/Container.vue';

definePage({
  name: 'recommendations',
});

const user = useDebugUser();
const loading = ref(true);
const error = ref<string | null>(null);
const errorType = ref<'auth' | 'data' | 'system' | null>(null);

const recommendations = ref<Recommendations | null>(null);
onMounted(async () => {
  try {
    if (!user.value?.uid) {
      error.value = 'Please log in to view recommendations';
      errorType.value = 'auth';
      loading.value = false;
      return;
    }
    const getRecommendations = httpsCallable<{ userId: string }, Recommendations>(functions, 'getRecommendations');
    const result = await getRecommendations({ userId: user.value.uid });
    const data = result.data as any;

    recommendations.value = data;
  }
  catch (err: any) {
    console.error('Error fetching recommendations:', err);
    captureException(err);
    // Handle Firebase Functions errors
    if (err?.code === 'functions/failed-precondition') {
      errorType.value = 'data';
      error.value
        = err?.message || 'No data available for recommendations. Please upload some golf session data first.';
    }
    else if (err?.code === 'functions/unauthenticated') {
      errorType.value = 'auth';
      error.value = 'Please log in to view recommendations';
    }
    else {
      errorType.value = 'system';
      error.value = err?.message || 'Failed to load recommendations';
    }
  }
  finally {
    loading.value = false;
  }
});
</script>

<template>
  <Container>
    <template #title>
      AI Recommendations
    </template>

    <div v-if="loading" class="flex min-h-[80vh] items-center justify-center flex-col gap-2">
      <div class="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      <p class="text-gray-900 dark:text-gray-200">
        Generating recommendations...
      </p>
    </div>

    <div
      v-else-if="error"
      class="rounded-md p-4"
      :class="{
        'bg-red-50': errorType === 'system',
        'bg-yellow-50': errorType === 'data',
        'bg-blue-50': errorType === 'auth',
      }"
    >
      <div class="flex">
        <div class="flex-shrink-0">
          <!-- System Error Icon -->
          <svg
            v-if="errorType === 'system'"
            class="h-5 w-5 text-red-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
          <!-- Data Error Icon -->
          <svg
            v-else-if="errorType === 'data'"
            class="h-5 w-5 text-yellow-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <!-- Auth Error Icon -->
          <svg
            v-else
            class="h-5 w-5 text-blue-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <h3
            class="text-sm font-medium"
            :class="{
              'text-red-800': errorType === 'system',
              'text-yellow-800': errorType === 'data',
              'text-blue-800': errorType === 'auth',
            }"
          >
            {{
              errorType === 'system' ? 'Error' : errorType === 'data' ? 'No Data Available' : 'Authentication Required'
            }}
          </h3>
          <div
            class="mt-2 text-sm"
            :class="{
              'text-red-700': errorType === 'system',
              'text-yellow-700': errorType === 'data',
              'text-blue-700': errorType === 'auth',
            }"
          >
            <p>{{ error }}</p>
            <div v-if="errorType === 'data'" class="mt-3">
              <router-link
                to="/upload"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Upload Golf Session Data
              </router-link>
            </div>
            <div v-if="errorType === 'auth'" class="mt-3">
              <router-link
                to="/login"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Log In
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- Key Focus Areas -->
      <div class="bg-white p-6 rounded-lg shadow mb-8">
        <h2 class="text-xl font-semibold mb-4">
          Key Focus Areas
        </h2>
        <div class="space-y-8">
          <!-- Content here -->
          <!-- High Priority Areas -->
          <div v-if="recommendations?.keyFocusAreas.highPriority?.length" class="mb-6">
            <h3 class="text-lg font-medium text-red-700 mb-3">
              High Priority
            </h3>
            <div class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              <div
                v-for="area in recommendations.keyFocusAreas.highPriority"
                :key="area.title"
                class="bg-red-50 border border-red-200 rounded-lg p-4 relative"
              >
                <div
                  class="absolute top-3 right-3 bg-red-100 text-red-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium"
                >
                  {{ area.priority }}
                </div>
                <h4 class="text-red-900 font-medium mb-1">
                  {{ area.title }}
                </h4>
                <p class="text-red-700 text-sm">
                  {{ area.description || 'Immediate attention needed' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Medium Priority Areas -->
          <div v-if="recommendations?.keyFocusAreas.mediumPriority?.length" class="mb-6">
            <h3 class="text-lg font-medium text-yellow-700 mb-3">
              Medium Priority
            </h3>
            <div class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              <div
                v-for="area in recommendations.keyFocusAreas.mediumPriority"
                :key="area.title"
                class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 relative"
              >
                <div
                  class="absolute top-3 right-3 bg-yellow-100 text-yellow-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium"
                >
                  {{ area.priority }}
                </div>
                <h4 class="text-yellow-900 font-medium mb-1">
                  {{ area.title }}
                </h4>
                <p class="text-yellow-700 text-sm">
                  {{ area.description || 'Ongoing improvement needed' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Low Priority Areas -->
          <div v-if="recommendations?.keyFocusAreas.lowPriority?.length" class="mb-6">
            <h3 class="text-lg font-medium text-green-700 mb-3">
              Low Priority
            </h3>
            <div class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              <div
                v-for="area in recommendations.keyFocusAreas.lowPriority"
                :key="area.title"
                class="bg-green-50 border border-green-200 rounded-lg p-4 relative"
              >
                <div
                  class="absolute top-3 right-3 bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium"
                >
                  {{ area.priority }}
                </div>
                <h4 class="text-green-900 font-medium mb-1">
                  {{ area.title }}
                </h4>
                <p class="text-green-700 text-sm">
                  {{ area.description || 'Maintain current level' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Long Term Improvement Goals -->
      <div class="bg-white p-6 rounded-lg shadow mb-8">
        <h2 class="text-xl font-semibold mb-4">
          Long Term Improvement Goals
        </h2>
      </div>
    </template>
  </Container>
</template>

<style scoped>
.recommendations-container {
  max-width: 1200px;
  margin: 0 auto;
}
</style>
